<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Receita' : 'Nova Receita' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label class="form-label">Descrição</label>
          <input v-model="receita.descricao" type="text" required placeholder="Ex: Salário Mensal"
            class="form-input" />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label class="form-label">Valor (R$)</label>
            <input v-model.number="receita.valor" type="number" step="0.01" required placeholder="0.00"
              class="form-input" />
          </div>
          <div>
            <label class="form-label">Data de Recebimento</label>
            <input v-model="receita.dataRecebimento" type="date" required class="form-input" />
          </div>
        </div>
        <div>
          <label class="form-label">Status</label>
          <select v-model="receita.status" required class="form-select">
            <option value="PENDENTE">Pendente</option>
            <option value="RECEBIDO">Recebido</option>
          </select>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const route = useRoute()

const isEditing = computed(() => !!route.params.id)
const isSubmitting = ref(false)
const getInitialReceita = () => ({
  id: null, descricao: '', valor: null, dataRecebimento: new Date().toISOString().split('T')[0], status: 'PENDENTE',
})
const receita = ref(getInitialReceita())

const apiUrl = 'https://backend.controlease.com.br/receitas';
const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const fetchReceita = async () => {
  if (!isEditing.value) return
  try {
    const { data } = await axios.get(`${apiUrl}/${route.params.id}`, authConfig())
    receita.value = data
  } catch {
    alert('Erro ao carregar receita.')
    router.push('/user/receitas')
  }
}

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${receita.value.id}`, receita.value, authConfig())
    } else {
      await axios.post(apiUrl, receita.value, authConfig())
    }
    router.push('/user/receitas')
  } catch {
    alert('Não foi possível salvar a receita.')
  } finally {
    isSubmitting.value = false;
  }
}

const goBack = () => {
  router.go(-1)
}

onMounted(() => {
  if (isEditing.value) {
    fetchReceita()
  }
})
</script>

<style scoped>
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20;
}
.btn {
  @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60;
}
.btn-primary {
  @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20;
}
.btn-secondary {
  @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm;
}
.form-input,
.form-select {
  @apply w-full px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-800/70 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] focus:bg-white dark:focus:bg-slate-900;
}
.form-select { @apply appearance-none; }
.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5;
}
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>